// src/pages/ManageStatuses.tsx
import { useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/ui/PageHeader';
import {
  Box, Paper, Stack, TextField, Button, IconButton, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, ListItemSecondaryAction, Divider,
  InputAdornment, Skeleton
} from '@mui/material';
import { Add, Edit, Delete as DeleteIcon, SearchRounded } from '@mui/icons-material';
import { listStatuses, createStatus, updateStatus, deleteStatus } from '../services/api';
import Swal from 'sweetalert2';
import { Checkbox } from '@mui/material';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkSelectBar from '../components/ui/BulkSelectBar';

type Item = { _id: string; name: string };

export default function ManageStatuses() {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const bulk = useBulkSelect();

  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState<Item | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(i => i.name.toLowerCase().includes(q));
  }, [items, search]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listStatuses();
      setItems(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setError('');
    setDlgOpen(true);
  };
  const openEdit = (it: Item) => {
    setEditing(it);
    setName(it.name);
    setError('');
    setDlgOpen(true);
  };
  const closeDlg = () => setDlgOpen(false);

  const save = async () => {
    const v = name.trim();
    if (!v) return setError('Name is required');
    try {
      if (editing) await updateStatus(editing._id, v);
      else await createStatus(v);
      closeDlg();
      await load();
      Swal.fire('Success', `Status ${editing ? 'updated' : 'added'} successfully`, 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to save';
      setError(msg);
      Swal.fire('Error', msg, 'error');
    }
  };

  const remove = async (id: string) => {
    const result = await Swal.fire({
      title: 'Delete status?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;
    try {
      await deleteStatus(id);
      await load();
      Swal.fire('Deleted', 'Status deleted successfully', 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to delete';
      Swal.fire('Error', msg, 'error');
    }
  };

  return (
    <Box>
      <Box mb={2}>
        <PageHeader title="Statuses" />
        <Typography color="text.secondary">Define and manage your workflow statuses.</Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth size="small" placeholder="Search statuses…"
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            )}}
          />
          <Button startIcon={<Add />} variant="contained" onClick={openCreate}>Add</Button>
        </Stack>
      </Paper>

      <Paper sx={{ borderRadius: 1, overflow: 'hidden' }}>
        {loading ? (
          <Box p={2}>
            {[0, 1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: i === 3 ? 0 : 1.5,
                }}
              >
                <Skeleton variant="circular" width={32} height={32} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton height={14} width="55%" />
                  <Skeleton height={12} width="35%" />
                </Box>
                <Skeleton variant="rounded" width={64} height={32} />
              </Box>
            ))}
          </Box>
        ) : filtered.length === 0 ? (
          <Box p={3} color="text.secondary">No items.</Box>
        ) : (
          <>
          <Box sx={{ px: 2, pt: 0.5, borderBottom: 1, borderColor: 'divider' }}>
            <BulkSelectBar bulk={bulk} visibleIds={filtered.map((it) => it._id)} noun="status" nounPlural="statuses" deleteOne={deleteStatus} onDone={load} />
          </Box>
          <List dense>
            {filtered.map((it, idx) => (
              <Box key={it._id}>
                {idx > 0 && <Divider />}
                <ListItem>
                  <Checkbox size="small" edge="start" checked={bulk.isSelected(it._id)} onChange={() => bulk.toggle(it._id)} sx={{ mr: 1 }} />
                  <ListItemText primary={it.name} />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => openEdit(it)} sx={{ mr: 0.5 }}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => remove(it._id)} sx={{ color: 'error.main' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </Box>
            ))}
          </List>
          </>
        )}
      </Paper>

      <Dialog open={dlgOpen} onClose={closeDlg} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Edit' : 'Add'} Status</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Name" value={name}
            onChange={e => { setName(e.target.value); setError(''); }}
            autoFocus fullWidth placeholder="e.g., Pending / In Progress / Done"
            error={!!error} helperText={error}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDlg} variant="outlined">Cancel</Button>
          <Button onClick={save} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
